import { memo, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { TaskType } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useCallIntelDefaultsShallowStore } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { getFilteredEnumValues } from '../../../utils/call-intelligence';
import { useActiveFilters } from '../hooks/use-active-filters';
import { getAllInferencesList } from './format-chart-data';
import { getChartConfig } from './get-chart-config';
import { InViewLocationsChartStats } from './in-view-locations-chart-stats';
import { FormattedChartData, InferencesChartData, LocationComparisonChartsProps, StatsType } from './types';
import { useAggregateLoading } from './use-aggregate-loading';
import { useChartDataFormatter } from './use-chart-data-formatter';

const ChartContainer = memo(
  ({
    children,
    divRef,
    id,
    isLoading,
    track,
  }: {
    children: ReactNode;
    divRef: (node?: Element | null) => void;
    id: string;
    isLoading: boolean;
    track: (chartId: string, loading: boolean) => void;
  }) => {
    useEffect(() => {
      track(id, isLoading);
    }, [id, isLoading, track]);

    return <div ref={divRef}>{children}</div>;
  }
);

ChartContainer.displayName = 'ChartContainer';

export const LocationComparisonCharts = ({
  drillDownFilters,
  internalPage,
  onChangeLoadingState,
  onClickSummaryCard,
}: LocationComparisonChartsProps) => {
  const { t } = useTranslation('analytics');
  const { dataLabels, dataColors } = useCallIntelDefaultsShallowStore('dataLabels', 'dataColors');
  const { filters } = useActiveFilters();
  const { track } = useAggregateLoading(onChangeLoadingState);
  const { formatInferences, formatOpportunities, formatScheduling } = useChartDataFormatter();

  const drillDownKey = drillDownFilters?.key || '';
  const drillDownValue = drillDownFilters?.value?.[0] || '';

  const opportunitiesChartTitle = useMemo(() => {
    const drillDownLabel =
      drillDownKey === 'appointment_types'
        ? dataLabels.appointmentTypes?.[drillDownValue]
        : drillDownKey === 'categories'
        ? dataLabels.categories?.[drillDownValue]
        : dataLabels.sentiments?.[drillDownValue];

    if (drillDownKey) {
      return t('{{value}} Calls - Scheduling Outcomes', { value: drillDownLabel });
    }
    return t('Scheduling Leaderboard');
  }, [dataLabels.appointmentTypes, dataLabels.categories, dataLabels.sentiments, drillDownKey, drillDownValue, t]);

  const chartItems = useMemo(
    () =>
      getChartConfig({
        dataColors,
        dataLabels,
        drillDownKey,
        drillDownValue,
        internalPage,
        onClickSummaryCard,
        opportunitiesChartTitle,
      }),
    [dataColors, dataLabels, drillDownKey, drillDownValue, internalPage, onClickSummaryCard, opportunitiesChartTitle]
  );

  const createFormatter = useCallback(
    (id: StatsType) =>
      ({
        analysisData,
        depsData,
      }: {
        analysisData: CallIntelTypes.CallsViewByLocation[keyof CallIntelTypes.CallsViewByLocation];
        depsData: Record<StatsType, CallIntelTypes.CallsViewByLocation[keyof CallIntelTypes.CallsViewByLocation]>;
      }): FormattedChartData => {
        switch (id) {
          case 'schedulingStats':
            return formatScheduling(analysisData as CallIntelTypes.CallsViewByLocation['schedulingStatsByLocation']);
          case 'opportunities': {
            const sentimentsByLocation = formatInferences(
              depsData.sentiments as Record<string, Record<string, number>>,
              [
                CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE,
                CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL,
                CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE,
              ]
            );
            return formatOpportunities(
              analysisData as CallIntelTypes.CallsViewByLocation['opportunitiesByLocation'],
              sentimentsByLocation
            );
          }
          case 'taskTypes':
            return formatInferences(
              analysisData as Record<string, Record<string, number>>,
              getFilteredEnumValues(TaskType, TaskType.TYPE_UNKNOWN)
            );
          case 'categories':
            return formatInferences(
              analysisData as Record<string, Record<string, number>>,
              getAllInferencesList(analysisData as Record<string, Record<string, number>>)
            );
          case 'appointmentTypes':
            return formatInferences(
              analysisData as Record<string, Record<string, number>>,
              getAllInferencesList(analysisData as Record<string, Record<string, number>>)
            );
          case 'sentiments':
            return formatInferences(analysisData as Record<string, Record<string, number>>, [
              CallIntelTypes.SentimentEnum.SENTIMENT_POSITIVE,
              CallIntelTypes.SentimentEnum.SENTIMENT_NEUTRAL,
              CallIntelTypes.SentimentEnum.SENTIMENT_NEGATIVE,
            ]);
          default:
            return {} as InferencesChartData;
        }
      },
    [formatInferences, formatOpportunities, formatScheduling]
  );

  return (
    <>
      {chartItems
        .filter((chart) => chart.shouldRender)
        .map(({ dependencies, id, renderChart }) => (
          <InViewLocationsChartStats
            analysis={id}
            key={id}
            dependencies={dependencies}
            drillDownOptions={drillDownFilters}
            filters={filters}
            formatter={createFormatter(id)}
            render={({ data, isLoading, ref }) => (
              <ChartContainer id={id} isLoading={isLoading} track={track} divRef={ref}>
                {renderChart(data as FormattedChartData, isLoading)}
              </ChartContainer>
            )}
          />
        ))}
    </>
  );
};
